import type { Emoji } from '@emoji-mart/data'
import data from '@emoji-mart/data'
import { useForwardedRef } from '@jotta/hooks'
import * as Popover from '@radix-ui/react-popover'
import { SearchIndex, init } from 'emoji-mart'
import type { PropsWithChildren } from 'react'
import { forwardRef, useEffect, useState } from 'react'
import { useKey, useKeyPressEvent } from 'react-use'

init({ data })

export const EmojiSearch = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    search: string
    onOpenChange: (open: boolean) => void
    onSubmit?: (emoji: Emoji) => void
  }>
>(function EmojiSearch({ search, onOpenChange, onSubmit, children }, ref) {
  const [selected, setSelected] = useState(0)
  const [results, setResults] = useState<Emoji[]>([])
  const [open, setOpen] = useState(false)

  const popover = useForwardedRef(ref)

  const onOpenChangeWrapper = (open: boolean) => {
    if (open) {
      // Prevent trigger from opening this on click
      return
    }
    onOpenChange(false)
  }

  useEffect(() => {
    const val = results.length > 0

    if (val === open) {
      return
    }

    onOpenChange(val)
    setOpen(val)
  }, [results, onOpenChange, open])

  useEffect(() => {
    setSelected(0)
  }, [results])

  useEffect(() => {
    if (!search) {
      setResults([])
      return
    }

    SearchIndex.search(search).then((res: Emoji[]) => {
      setResults(res)
    })
  }, [search])

  useKey(
    'ArrowUp',
    e => {
      if (!results || !results.length) {
        return
      }

      const next = (selected || results.length) - 1
      setSelected(next)
      e.preventDefault()
      popover.current?.children[next].scrollIntoView({ block: 'nearest' })
    },
    undefined,
    [results, selected],
  )

  useKey(
    'ArrowDown',
    e => {
      if (!results || !results.length) {
        return
      }

      const next = (selected + 1) % results.length
      setSelected(next)
      e.preventDefault()
      popover.current?.children[next].scrollIntoView({ block: 'nearest' })
    },
    undefined,
    [results, selected],
  )

  const submit = (emoji: Emoji) => {
    if (onSubmit) {
      onSubmit(emoji)
    }
    onOpenChange(false)
  }

  useKeyPressEvent('Enter', e => {
    const emoji = results[selected]

    if (emoji) {
      e.preventDefault()
      submit(emoji)
    }
  })

  return (
    <Popover.Root open={open} onOpenChange={onOpenChangeWrapper} modal={false}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Content
        ref={popover}
        onOpenAutoFocus={e => e.preventDefault()}
        sideOffset={10}
        sx={{
          width: '376px',
          maxHeight: '198px',
          border: '1px solid rgb(218, 218, 218)',
          backgroundColor: 'white',
          borderRadius: '3px',
          fontSize: 2,
          overflowY: 'auto',
          overflowX: 'hidden',
          cursor: 'pointer',
          zIndex: 10,
        }}
      >
        {results.map((emoji, i) => (
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'stretch',
              gap: 6,
              color: 'var(--color-text)',
              ...(selected !== null &&
                selected === i && {
                  backgroundColor: 'var(--color-selection)',
                  color: 'white',
                }),
            }}
            onMouseDown={e => {
              e.preventDefault()
            }}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              submit(emoji)
            }}
            onMouseMove={() => {
              if (selected !== i) {
                setSelected(i)
              }
            }}
            key={emoji.id}
          >
            <div style={{ fontSize: '24px' }}>{emoji.skins[0].native}</div>
            <div>
              {(emoji.aliases && `:${emoji.aliases[0]}:`) ||
                emoji.skins[0].shortcodes}
            </div>
          </button>
        ))}
      </Popover.Content>
    </Popover.Root>
  )
})
